import React, { Component } from "react";
import IconButton from "@material-ui/core/IconButton";
import Badge from "@material-ui/core/Badge";
import Popover from "@material-ui/core/Popover";
import Typography from "@material-ui/core/Typography";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";
import NotificationsIcon from "@material-ui/icons/Notifications";
import { Link } from "react-admin";
import { API_ROOT } from "../api-config";
import { hasRole, ROLE_IDS, canAccess } from "../rbac";
import ClientDocumentsModal from "./ClientDocumentsModal";

const POLL_MS = 5 * 60 * 1000;

class DocumentationReminderBell extends Component {
  state = {
    anchorEl: null,
    loading: false,
    count: 0,
    items: [],
    from: null,
    to: null,
    error: null,
    documentsModalOpen: false,
    documentsClientId: null,
    documentsRentalId: null,
  };

  componentDidMount() {
    if (!this.isVisible()) return;
    this.fetchPending();
    this.pollId = setInterval(this.fetchPending, POLL_MS);
  }

  componentWillUnmount() {
    if (this.pollId) clearInterval(this.pollId);
  }

  isVisible = () =>
    canAccess("documentation_reminders")
    || hasRole(ROLE_IDS.TOUR_MANAGER)
    || hasRole(ROLE_IDS.ADMIN)
    || hasRole(ROLE_IDS.MANAGER)
    || hasRole(ROLE_IDS.SUPER_ADMIN);

  fetchPending = async () => {
    if (!this.isVisible()) return;
    this.setState({ loading: true, error: null });
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_ROOT}/client_documents/pending_tour_rentals`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );
      if (!res.ok) {
        throw new Error("Could not load reminders");
      }
      const data = await res.json();
      this.setState({
        count: data.count || 0,
        items: data.items || [],
        from: data.from || null,
        to: data.to || null,
        loading: false,
      });
    } catch (err) {
      this.setState({
        loading: false,
        error: err.message || "Error",
      });
    }
  };

  handleOpen = event => {
    this.setState({ anchorEl: event.currentTarget });
    this.fetchPending();
  };

  handleClose = () => {
    this.setState({ anchorEl: null });
  };

  openDocuments = (item, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!item || !item.clientId) return;
    this.setState({
      documentsModalOpen: true,
      documentsClientId: item.clientId,
      documentsRentalId: item.useRentalDocuments ? item.rentalId : null,
      anchorEl: null,
    });
  };

  closeDocuments = () => {
    this.setState({
      documentsModalOpen: false,
      documentsClientId: null,
      documentsRentalId: null,
    });
    this.fetchPending();
  };

  render() {
    if (!this.isVisible()) return null;

    const {
      anchorEl,
      loading,
      count,
      items,
      from,
      to,
      error,
      documentsModalOpen,
      documentsClientId,
      documentsRentalId,
    } = this.state;
    const open = Boolean(anchorEl);

    return (
      <React.Fragment>
        <IconButton
          color="inherit"
          aria-label="Documentation reminders"
          title="Missing documentation"
          onClick={this.handleOpen}
          style={{ marginRight: 4 }}
        >
          <Badge
            badgeContent={count > 99 ? "99+" : count}
            color="error"
            invisible={count < 1}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={this.handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <div style={{ width: 360, maxHeight: 420, overflow: "auto" }}>
            <div style={{ padding: "12px 16px 8px" }}>
              <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                Missing documentation
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Tours and rentals missing passport or licence photos
                {from && to ? ` · ${from} – ${to}` : ""}
              </Typography>
            </div>
            <Divider />
            {loading && items.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center" }}>
                <CircularProgress size={28} />
              </div>
            ) : error ? (
              <Typography
                variant="body2"
                color="error"
                style={{ padding: 16 }}
              >
                {error}
              </Typography>
            ) : items.length === 0 ? (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ padding: 16 }}
              >
                No pending documentation reminders.
              </Typography>
            ) : (
              <List dense disablePadding>
                {items.map(item => {
                  const isPlainRental = Number(item.type) === 2;
                  const recordLabel = isPlainRental ? "Rental" : "Tour rental";
                  const recordPath = isPlainRental
                    ? `/rental_clients/${item.rentalId}/show`
                    : `/tour_clients/${item.rentalId}/show`;
                  return (
                    <ListItem
                      key={item.rentalId}
                      button
                      onClick={e => this.openDocuments(item, e)}
                      style={{ alignItems: "flex-start" }}
                    >
                      <ListItemText
                        primary={item.clientName}
                        secondary={
                          <React.Fragment>
                            <span>
                              {recordLabel} #{item.rentalId}
                              {item.date1 ? ` · ${item.date1}` : ""}
                            </span>
                            <br />
                            <span>
                              Passport: {item.passport ? "OK" : "missing"}
                              {" · "}
                              Licence: {item.driving_licence ? "OK" : "missing"}
                            </span>
                            <br />
                            <Link
                              to={recordPath}
                              onClick={e => e.stopPropagation()}
                              style={{ fontSize: 12 }}
                            >
                              Open rental
                            </Link>
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </div>
        </Popover>

        <ClientDocumentsModal
          open={documentsModalOpen}
          onClose={this.closeDocuments}
          clientId={documentsClientId}
          rentalId={documentsRentalId}
          allowUpload
        />
      </React.Fragment>
    );
  }
}

export default DocumentationReminderBell;



// WEBPACK FOOTER //
// ./src/components/DocumentationReminderBell.js